import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const ContactPage = () => {
  const [formData, setFormData] = useState({ naam: '', email: '', onderwerp: '', bericht: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSubmitted(true);
  };

  const contactItems = [
    { icon: "Mail", label: "E-mail algemeen", value: "contact@verduurzaamwijzer.nu", href: "mailto:contact@verduurzaamwijzer.nu" },
    { icon: "Mail", label: "E-mail advies", value: "advies@verduurzaamwijzer.nu", href: "mailto:advies@verduurzaamwijzer.nu" },
    { icon: "Phone", label: "Telefoon", value: "06 4765 0537", href: "tel:0647650537" },
    { icon: "MapPin", label: "Bezoekadres", value: "Oude Middenweg 17\n2491 AC Den Haag", href: null },
    { icon: "Clock", label: "Bereikbaarheid", value: "Ma–Vr: 09:00–17:00", href: null },
  ];

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/8 via-background to-background pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-foreground/50 mb-4">
              <Link to="/homepage" className="hover:text-primary transition-colors">Home</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground/70">Contact</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="MessageSquare" size={20} className="text-primary" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">Contact</h1>
            </div>
            <p className="text-foreground/70 text-lg leading-relaxed max-w-2xl">
              Heeft u een vraag, opmerking of wilt u samenwerken? Wij staan voor u klaar.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Contact info sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-6">
                <div>
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-4">Contactgegevens</h2>
                  <div className="space-y-4">
                    {contactItems?.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name={item?.icon} size={15} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-xs text-foreground/50 mb-0.5">{item?.label}</p>
                          {item?.href ? (
                            <a href={item?.href} className="text-sm text-foreground/80 hover:text-primary transition-colors whitespace-pre-line">
                              {item?.value}
                            </a>
                          ) : (
                            <p className="text-sm text-foreground/80 whitespace-pre-line">{item?.value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-border/40 pt-5">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Bedrijfsgegevens</h2>
                  <div className="text-xs text-foreground/60 space-y-1">
                    <p className="font-medium text-foreground/70">De Content Factory BV</p>
                    <p>KvK: 84512490</p>
                    <p>Btw: 8632.40.100.B.01.</p>
                  </div>
                </div>
              </div>
            </aside>

            {/* Contact form */}
            <main className="flex-1 min-w-0">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="bg-green-50 border border-green-200 rounded-xl p-8 text-center"
                >
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircle" size={24} className="text-green-600" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-foreground mb-2">Bericht verzonden!</h2>
                  <p className="text-foreground/70 text-sm mb-4">
                    Bedankt voor uw bericht. Wij reageren binnen 2 werkdagen.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ naam: '', email: '', onderwerp: '', bericht: '' }); }}
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    Nog een bericht sturen
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <h2 className="font-heading text-lg font-semibold text-foreground mb-6 pb-2 border-b border-border/50">
                    Stuur ons een bericht
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-1.5" htmlFor="naam">
                          Naam <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="naam"
                          name="naam"
                          type="text"
                          required
                          value={formData?.naam}
                          onChange={handleChange}
                          placeholder="Uw volledige naam"
                          className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground/80 mb-1.5" htmlFor="email">
                          E-mailadres <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData?.email}
                          onChange={handleChange}
                          placeholder="uw@email.nl"
                          className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-1.5" htmlFor="onderwerp">
                        Onderwerp <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="onderwerp"
                        name="onderwerp"
                        required
                        value={formData?.onderwerp}
                        onChange={handleChange}
                        className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                      >
                        <option value="">Selecteer een onderwerp</option>
                        <option value="advies">Advies over verduurzaming</option>
                        <option value="aanbieder">Aanbieder worden</option>
                        <option value="klacht">Klacht over aanbieder</option>
                        <option value="samenwerking">Samenwerking / pers</option>
                        <option value="technisch">Technische vraag</option>
                        <option value="overig">Overig</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground/80 mb-1.5" htmlFor="bericht">
                        Bericht <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="bericht"
                        name="bericht"
                        required
                        rows={6}
                        value={formData?.bericht}
                        onChange={handleChange}
                        placeholder="Schrijf hier uw bericht..."
                        className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <p className="text-xs text-foreground/50">
                        Wij reageren binnen 2 werkdagen. Zie ook onze{' '}
                        <Link to="/privacy" className="text-primary hover:underline">privacyverklaring</Link>.
                      </p>
                      <motion.button
                        type="submit"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        <Icon name="Send" size={15} />
                        Versturen
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
              )}
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
